module Index

open Client
open Client.Components
open Client.DomainTypes
open Client.Domain
open Client.Infrastructure.Api
open Client.Infrastructure.Session
open Client.Model
open Client.DomainTypes.Msg
open Client.Session
open Client.Page
open Client.Page.Admin
open Client.Page.User
open Fable.Core.JsInterop
open Elmish
open Fable.FontAwesome
open Fable.React
open Fulma
open Leaflet
open Shared.Dto.Dto
open Thoth.Elmish

importAll "flatpickr/dist/themes/material_green.css"
importAll "../../node_modules/leaflet/dist/leaflet.css"
icon?Default?imagePath <- "//cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/"

importAll "./Styles/main.css"
importAll "./Styles/map.css"
importAll "./Styles/graph.css"


let private mapModel (modelMap: 'model -> PageModel) (subModel: 'model, cmd: Cmd<Msg>) = modelMap subModel, cmd

let onlyLoggedIn route =
    onlyForLoggedInSession (Login.init route >> mapModel PageModel.Login)

let private getPageModelWithFeatureFlag
    (featureFlag: string)
    (f: UserSession -> PageModel * Cmd<Msg>)
    (session: UserSession)
    =
    if userHasFeatureFlag featureFlag session.User then
        f session
    else
        PageModel.NotAllowed, Cmd.none

let urlUpdate (maybeRoute: Route option) (model: Model) : Model * Cmd<Msg> =
    let route = Option.defaultValue Route.NotFound maybeRoute

    let map (modelMap: 'model -> PageModel) (cmdMap: 'msg -> Msg) (subModel: 'model, subCmd: Cmd<'msg>) =
        modelMap subModel, Cmd.map cmdMap subCmd

    let onlyWithSession =
        initWithSession (PendingSession.init route |> PageModel.PendingSession, Cmd.none) model.Session

    let pageModel, pageCmd =
        match route with
        | Route.Home -> onlyWithSession (onlyLoggedIn route (Home.init >> mapModel PageModel.Home))
        | Route.TtnSensorList ->
            onlyWithSession (onlyLoggedIn route (PhysicalSensorList.init >> mapModel PageModel.TtnSensorList))
        | Route.MySensSensorList ->
            onlyWithSession (onlyLoggedIn route (MapSensorList.init >> mapModel PageModel.MySensSensorList))
        | Route.GatewayList -> onlyWithSession (onlyLoggedIn route (GatewayList.init >> mapModel PageModel.GatewayList))
        | Route.PublicMap -> onlyWithSession (fun _ -> PublicMap.init |> map PublicPage PublicMap)
        | Route.SensorMap _ -> onlyWithSession (onlyLoggedIn route (SensorMap.init >> map PageModel.SensorPage Msg.Map))
        | Route.Login -> onlyWithSession (Login.init Route.Home >> mapModel Model.Login)
        | Route.NotFound -> onlyWithSession (fun _ -> NotFound, Cmd.none)
        | Route.PasswordLost -> onlyWithSession (fun _ -> PageModel.PasswordLost PasswordLost.init, Cmd.none)
        | Route.ResetPassword token -> onlyWithSession (ResetPassword.init token >> mapModel Model.ResetPassword)
        | Route.MyProfile -> onlyWithSession (onlyLoggedIn route (MyProfile.init >> mapModel PageModel.MyProfile))
        | Route.UserList -> onlyWithSession (onlyLoggedIn route (UserList.init >> mapModel PageModel.UserList))
        | Route.CalculationConfiguration ->
            onlyWithSession (
                onlyLoggedIn route (CalculationConfiguration.init >> mapModel PageModel.CalculationConfiguration)
            )
        | Route.MySensSensor id ->
            onlyWithSession (onlyLoggedIn route (MapSensorDataPage.init id >> mapModel PageModel.MapSensorData))
        | Route.AlertsList -> onlyWithSession (onlyLoggedIn route (AlertsList.init >> mapModel PageModel.AlertsList))
        | Route.MyAlerts -> onlyWithSession (onlyLoggedIn route (MyAlerts.init >> mapModel PageModel.MyAlerts))
        | Route.MySensorsData ->
            onlyWithSession (onlyLoggedIn route (Page.User.MySensorsData.init >> mapModel PageModel.MySensorsData))
        | Route.MyMapSensors ->
            onlyWithSession (onlyLoggedIn route (Page.User.MyMapSensors.init >> mapModel PageModel.MyMapSensors))
        | Route.RimPro ->
            let f = Page.User.RimPro.init >> mapModel PageModel.RimPro

            onlyWithSession (onlyLoggedIn route f)
        | Route.MyGateways ->
            onlyWithSession (onlyLoggedIn route (Page.User.MyGateways.init >> mapModel PageModel.MyGateways))
        | Route.MyScabStations ->
            onlyWithSession (onlyLoggedIn route (Page.User.MyScabStations.init >> mapModel PageModel.MyScabStations))
        | Route.ScabData stationId ->
            onlyWithSession (onlyLoggedIn route (Page.User.ScabData.init stationId >> mapModel PageModel.ScabData))
        | Route.MyUserGroups ->
            onlyWithSession (onlyLoggedIn route (Page.User.MyUserGroups.init >> mapModel PageModel.MyUserGroups))
        | Route.UserDefinedMapSensorProperties id ->
            onlyWithSession (
                onlyLoggedIn
                    route
                    (Page.User.UserDefinedMapSensorProperties.init id
                     >> mapModel PageModel.UserDefinedMapSensorSettings)
            )
        | Route.MyPeronosporaStations ->
            onlyWithSession (
                onlyLoggedIn route (Page.User.MyPeronosporaStations.init >> mapModel PageModel.MyPeronosporaStations)
            )
        | Route.PeronosporaData stationId ->
            onlyWithSession (
                onlyLoggedIn route (Page.User.PeronosporaData.init stationId >> mapModel PageModel.PeronosporaData)
            )
        | Route.GroupList -> onlyWithSession (onlyLoggedIn route (GroupList.init >> mapModel PageModel.GroupList))
        | Route.MyTalgilTriggers ->
            let f = MyTalgilTriggers.init >> mapModel PageModel.MyTalgilTriggers

            onlyWithSession (onlyLoggedIn route f)
        | Route.MyTipBurnStations ->
            onlyWithSession (
                onlyLoggedIn route (Page.User.MyTipburnStations.init >> mapModel PageModel.MyTipBurnStations)
            )
        | Route.TipBurnData stationId ->
            onlyWithSession (onlyLoggedIn route (TipBurnData.init stationId >> mapModel PageModel.TipBurnData))
        | Route.ReceivedSensorPackets sensorId ->
            onlyWithSession (
                onlyLoggedIn route (ReceivedSensorPackets.init sensorId >> mapModel PageModel.ReceivedSensorPackets)
            )
        | Route.TalgilTriggerExecutionLog triggerId ->
            onlyWithSession (
                onlyLoggedIn
                    route
                    (TalgilTriggerExecutionLog.init triggerId
                     >> mapModel PageModel.TalgilTriggerExecutionLog)
            )

    { model with Page = pageModel }, pageCmd

let heroView (content: ReactElement) =
    Hero.hero [ Hero.IsFullHeight ] [
        content
        Hero.foot [] [ PageSkeleton.mySensFooter ]
    ]

let mapCmd (model: Model) (modelMap: 'model -> PageModel) (cmdMap: 'msg -> Msg) (subModel: 'model, subCmd: Cmd<'msg>) =
    let newModel = { model with Page = modelMap subModel }

    newModel, Cmd.map cmdMap subCmd

let closeBurger (model: Model) = { model with NavbarModel = Navbar.initialNavbarModel }

let update (msg: Msg) (model: Model) : Model * Cmd<Msg> =
    let mapModel (modelMap: 'model -> PageModel) (subModel: 'model, cmd: Cmd<Msg>) = modelMap subModel, cmd

    match (msg, model.Page) with
    | Global LogoutPressed, _ ->
        match model.Session with
        | Pending -> model, Cmd.none
        | Session userSession -> model, runLogout userSession |> Cmd.map Global
    | Global LoggedOut, _ ->
        let session = Anonymous

        Page.Login.init Route.Home session |> mapCmd model Model.Login id
    | Global(SessionReceived session), PendingSession(PendingSession.Loading route) ->
        urlUpdate (Some route) { model with Session = Session session }
    | Global(GoToRoute route), _ ->
        model
        |> closeBurger
        |> (fun model ->
            let additionalCommands = Routing.routeToAdditionalCommands route
            let navCmd = route |> Routing.routeToUrl |> Navigation.Navigation.newUrl

            model, Cmd.batch [ navCmd; additionalCommands ]
        )
    | Global(Navbar subMsg), _ ->
        let newNavbarModel = Navbar.updateModel subMsg model.NavbarModel

        { model with NavbarModel = newNavbarModel }, Cmd.none
    | Global(GlobalMsg.Reload doReload), _ ->
        ErrorConfirmation.doReload doReload

        model, Cmd.none
    | Global(GlobalMsg.ImpersonationResult result), _ ->
        match result with
        | Ok true ->
            let toastCmd = Toast.create "Du hast dich erfolgreich eingeloggt" |> Toast.success

            reinitModel Route.Home toastCmd
        | _ ->
            let toastCmd = Toast.create "Das 'Einloggen als' ist fehlgeschlagen" |> Toast.error

            model, toastCmd
    | Global GlobalMsg.ImpersonationStopped, _ ->
        let toastCmd = Toast.create "Benutzeransicht erfolgreich beendet" |> Toast.success

        reinitModel Route.Home toastCmd
    | Global(GlobalMsg.StopImpersonation), _ ->
        let cmd =
            match model.Session with
            | Session(UserSession session) ->
                let requestData: AuthenticatedRequest<unit> = {
                    SessionKey = session.SessionKey
                    Data = ()
                }

                Cmd.OfAsync.perform api.stopImpersonation requestData (fun _ -> Global(GlobalMsg.ImpersonationStopped))
            | _ -> Cmd.none

        model, cmd
    | PublicMap pageMsg, PublicPage pageModel ->
        PublicMap.update pageMsg pageModel
        |> mapCmd model Model.PublicPage Msg.PublicMap
    | Map pageMsg, SensorPage pageModel -> SensorMap.update pageMsg pageModel |> mapCmd model Model.SensorPage Map
    | Login loginMsg, PageModel.Login loginModel ->
        let clientSession =
            match loginMsg with
            | LoginResult(Successful userSession) ->
                storeClientSessionId (sessionKeyToString userSession.SessionKey)

                Session(UserSession userSession)
            | _ -> model.Session

        Login.update loginMsg loginModel
        |> mapModel Model.Login
        |> fun (pageModel, cmd) ->
            {
                model with
                    Page = pageModel
                    Session = clientSession
            },
            cmd
    | Msg.Home dataMsg, PageModel.Home dataModel ->
        Page.Home.update dataMsg dataModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.Home pageModel }, cmd
    | PhysicalSensorList ttnMsg, PageModel.TtnSensorList listModel ->
        PhysicalSensorList.update ttnMsg listModel
        |> mapModel Model.TtnSensorList
        |> fun (pageModel, cmd) -> { model with Page = pageModel }, cmd
    | MapSensorList mySensMsg, PageModel.MySensSensorList listModel ->
        MapSensorList.update mySensMsg listModel
        |> mapModel Model.MySensSensorList
        |> fun (pageModel, cmd) -> { model with Page = pageModel }, cmd
    | GatewayList gatewayMsg, PageModel.GatewayList listModel ->
        GatewayList.update gatewayMsg listModel
        |> mapModel Model.GatewayList
        |> fun (pageModel, cmd) -> { model with Page = pageModel }, cmd
    | MyProfile myProfileMsg, PageModel.MyProfile profileModel ->
        MyProfile.update myProfileMsg profileModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MyProfile pageModel }, cmd
    | UserList userListMsg, PageModel.UserList userListModel ->
        UserList.update userListMsg userListModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.UserList pageModel }, cmd
    | PasswordLost lostMsg, PageModel.PasswordLost lostModel ->
        PasswordLost.update lostMsg lostModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.PasswordLost pageModel }, cmd
    | ResetPassword resetMsg, PageModel.ResetPassword resetModel ->
        ResetPassword.update resetMsg resetModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.ResetPassword pageModel }, cmd
    | CalculationConfiguration calcConfigurationMsg, PageModel.CalculationConfiguration calcConfigurationModel ->
        CalculationConfiguration.update calcConfigurationMsg calcConfigurationModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.CalculationConfiguration pageModel }, cmd
    | MapSensorData sensorMsg, PageModel.MapSensorData sensorModel ->
        MapSensorDataPage.update sensorMsg sensorModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MapSensorData pageModel }, cmd
    | AlertsList alertsMsg, PageModel.AlertsList alertsModel ->
        AlertsList.update alertsMsg alertsModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.AlertsList pageModel }, cmd
    | MyAlerts alertsMsg, PageModel.MyAlerts alertsModel ->
        MyAlerts.update alertsMsg alertsModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MyAlerts pageModel }, cmd
    | MySensorsData dataMsg, PageModel.MySensorsData dataModel ->
        MySensorsData.update dataMsg dataModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MySensorsData pageModel }, cmd
    | UserDefinedMapSensorProperties dataMsg, PageModel.UserDefinedMapSensorSettings dataModel ->
        UserDefinedMapSensorProperties.update dataMsg dataModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.UserDefinedMapSensorSettings pageModel }, cmd
    | Msg.MyMapSensors dataMsg, PageModel.MyMapSensors dataModel ->
        Page.User.MyMapSensors.update dataMsg dataModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MyMapSensors pageModel }, cmd
    | Msg.RimPro subMsg, PageModel.RimPro pageModel ->
        Page.User.RimPro.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.RimPro pageModel }, cmd
    | Msg.MyGateways subMsg, PageModel.MyGateways pageModel ->
        Page.User.MyGateways.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MyGateways pageModel }, cmd
    | Msg.ScabData subMsg, PageModel.ScabData pageModel ->
        Page.User.ScabData.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.ScabData pageModel }, cmd
    | Msg.MyScabStations subMsg, PageModel.MyScabStations pageModel ->
        Page.User.MyScabStations.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MyScabStations pageModel }, cmd
    | Msg.MyUserGroups subMsg, PageModel.MyUserGroups pageModel ->
        Page.User.MyUserGroups.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MyUserGroups pageModel }, cmd
    | Msg.MyPeronosporaStations subMsg, PageModel.MyPeronosporaStations pageModel ->
        Page.User.MyPeronosporaStations.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MyPeronosporaStations pageModel }, cmd
    | Msg.PeronosporaData subMsg, PageModel.PeronosporaData pageModel ->
        Page.User.PeronosporaData.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.PeronosporaData pageModel }, cmd
    | Msg.GroupList subMsg, PageModel.GroupList pageModel ->
        Page.Admin.GroupList.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.GroupList pageModel }, cmd
    | Msg.MyTalgilTriggers subMsg, PageModel.MyTalgilTriggers pageModel ->
        Page.User.MyTalgilTriggers.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MyTalgilTriggers pageModel }, cmd
    | Msg.MyTipBurnStations subMsg, PageModel.MyTipBurnStations pageModel ->
        Page.User.MyTipburnStations.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.MyTipBurnStations pageModel }, cmd
    | Msg.TipBurnData subMsg, PageModel.TipBurnData pageModel ->
        Page.User.TipBurnData.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.TipBurnData pageModel }, cmd
    | Msg.ReceivedSensorPackets subMsg, PageModel.ReceivedSensorPackets pageModel ->
        Page.User.ReceivedSensorPackets.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.ReceivedSensorPackets pageModel }, cmd
    | Msg.TalgilTriggerExecutionLog subMsg, PageModel.TalgilTriggerExecutionLog pageModel ->
        Page.User.TalgilTriggerExecutionLog.update subMsg pageModel
        |> fun (pageModel, cmd) -> { model with Page = PageModel.TalgilTriggerExecutionLog pageModel }, cmd
    | _, _ -> model, Cmd.none

let view (model: Model) (dispatch: Msg -> unit) =
    let user () =
        model.Session |> getUserFromClientSession |> Option.get

    let pageSkeletonFactory content =
        PageSkeleton.skeleton dispatch model.NavbarModel (user ()) content

    let pageSkeletonSingleElementFactory content = pageSkeletonFactory [ content ]

    match model.Page with
    | PublicPage pageModel -> PublicMap.view pageModel dispatch
    | SensorPage pageModel ->
        SensorMap.view pageModel dispatch
        |> PageSkeleton.skeletonOnlyNavbar dispatch model.NavbarModel (user ())
    | Model.Login pageModel -> Page.Login.view pageModel dispatch |> heroView
    | NotFound -> Page.NotFound.view |> heroView
    | NotAllowed -> Page.NotAllowed.view |> pageSkeletonSingleElementFactory
    | PendingSession _ -> Loadable.loadingView (Some "Warten auf Daten vom Server") Fa.Fa8x
    | Model.TtnSensorList listModel -> PhysicalSensorList.view listModel dispatch |> pageSkeletonSingleElementFactory
    | Model.MySensSensorList listModel ->
        Page.Admin.MapSensorList.view listModel dispatch
        |> pageSkeletonSingleElementFactory
    | Model.Home model -> Page.Home.view model dispatch |> pageSkeletonFactory
    | Model.MyProfile profileModel ->
        MyProfile.view (MyProfile >> dispatch) profileModel
        |> pageSkeletonSingleElementFactory
    | Model.UserList userListModel -> UserList.view userListModel dispatch |> pageSkeletonSingleElementFactory
    | Model.PasswordLost lostModel -> Page.PasswordLost.view lostModel dispatch |> heroView
    | Model.ResetPassword resetModel -> Page.ResetPassword.view resetModel dispatch |> heroView
    | Model.GatewayList listModel ->
        Page.Admin.GatewayList.view listModel dispatch
        |> pageSkeletonSingleElementFactory
    | Model.CalculationConfiguration calcConfigurationModel ->
        Page.Admin.CalculationConfiguration.view (CalculationConfiguration >> dispatch) calcConfigurationModel
        |> pageSkeletonSingleElementFactory
    | Model.MapSensorData sensorModel -> MapSensorDataPage.view sensorModel dispatch |> pageSkeletonSingleElementFactory
    | Model.AlertsList alertsModel ->
        Page.Admin.AlertsList.view alertsModel dispatch
        |> pageSkeletonSingleElementFactory
    | Model.MyAlerts alertsModel -> Page.User.MyAlerts.view alertsModel dispatch |> pageSkeletonFactory
    | Model.MySensorsData dataModel ->
        Page.User.MySensorsData.view dataModel dispatch
        |> pageSkeletonSingleElementFactory
    | Model.UserDefinedMapSensorSettings dataModel ->
        Page.User.UserDefinedMapSensorProperties.view dataModel dispatch
        |> pageSkeletonSingleElementFactory
    | Model.MyMapSensors dataModel ->
        Page.User.MyMapSensors.view dispatch dataModel
        |> pageSkeletonSingleElementFactory
    | Model.RimPro pageModel -> Page.User.RimPro.view dispatch pageModel |> pageSkeletonFactory
    | Model.MyGateways pageModel -> Page.User.MyGateways.view pageModel |> pageSkeletonSingleElementFactory
    | Model.ScabData pageModel -> Page.User.ScabData.view dispatch pageModel |> pageSkeletonSingleElementFactory
    | Model.MyScabStations pageModel ->
        Page.User.MyScabStations.view dispatch pageModel
        |> pageSkeletonSingleElementFactory
    | Model.MyUserGroups pageModel -> Page.User.MyUserGroups.view pageModel |> pageSkeletonSingleElementFactory
    | Model.MyPeronosporaStations pageModel ->
        Page.User.MyPeronosporaStations.view dispatch pageModel
        |> pageSkeletonSingleElementFactory
    | Model.PeronosporaData pageModel ->
        Page.User.PeronosporaData.view dispatch pageModel
        |> pageSkeletonSingleElementFactory
    | Model.GroupList pageModel -> Page.Admin.GroupList.view dispatch pageModel |> pageSkeletonFactory
    | Model.MyTalgilTriggers pageModel ->
        Page.User.MyTalgilTriggers.view dispatch pageModel
        |> pageSkeletonSingleElementFactory
    | Model.MyTipBurnStations pageModel ->
        Page.User.MyTipburnStations.view dispatch pageModel
        |> pageSkeletonSingleElementFactory
    | Model.TipBurnData pageModel ->
        Page.User.TipBurnData.view dispatch pageModel
        |> pageSkeletonSingleElementFactory
    | Model.ReceivedSensorPackets pageModel ->
        Page.User.ReceivedSensorPackets.view dispatch pageModel
        |> pageSkeletonSingleElementFactory
    | Model.TalgilTriggerExecutionLog pageModel ->
        Page.User.TalgilTriggerExecutionLog.view dispatch pageModel
        |> pageSkeletonSingleElementFactory